import { extractVideoReference } from './media';
import * as apiService from './api-service';
import * as db from './db-service';
import { getSteemConfig } from '../config';
import parse from "html-react-parser";

interface PaginationState {
  [tag: string]: {
    lastAuthor: string;
    lastPermlink: string;
    start?: number;
  };
}

let paginationCache: PaginationState = {};

function getYouTubeThumbnailUrl(videoId: string): string {
  return `https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`; // Puedes usar 'hqdefault.jpg' para mayor calidad
}
function extractYouTubeLink(body: string, metadata: any) {
  const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:m\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|shorts\/|v\/|watch\?v=))([a-zA-Z0-9_-]+)/;
  const match = body.match(youtubeRegex);
  let idString = null;
  let videoId = null;

  try {
    if (match && match[1]) {
      const shortMatch = match[0].match(/shorts\/([a-zA-Z0-9_-]+)/);
      idString = match[1];
      if (shortMatch) {
        videoId = match[0];
      } else {
        videoId = `https://www.youtube.com/embed/${idString}`;
      }
    }
    if (!videoId && metadata && metadata.video) {
      if (typeof metadata.video == 'string') {
        videoId = metadata.video;
      } else if (typeof metadata.video == 'object') {
        videoId = "https://www.youtube.com/embed/" + metadata.video.files.youtube;
      }
    }
    return {
      videoId,
      idString
    }
  } catch (error) {
    console.log("while getting video", error);
    return { videoId: false, idString: false }
  }
}
export function extractIPFSLink(body: string, metadata?: any): string | null {
  return extractVideoReference(body, metadata);
}

function cleanHTML(html: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  return doc.body.innerHTML;
}
export function renderHTMLSafe(rawHtml: string) {
  const cleaned = cleanHTML(rawHtml);
  return parse(cleaned);
}
async function fetchPostsByTag(
  tag: string,
  limit: number,
  filter: string = 'trending',
  resetPagination: boolean = false,
  inputLastAuthor: string = '',
  inputLastPermlink: string = '',
  method: string = 'get_ranked_posts',
  plugin = "bridge"
): Promise<{ posts: any[], lastAuthor: string, lastPermlink: string, hasMore: boolean }> {
  if (resetPagination || !paginationCache[tag]) {
    paginationCache[tag] = { lastAuthor: "", lastPermlink: "" };
  }

  const startAuthor = resetPagination ? '' : (inputLastAuthor || paginationCache[tag].lastAuthor);
  const startPermlink = resetPagination ? '' : (inputLastPermlink || paginationCache[tag].lastPermlink);

  let body;
  if (method == "get_ranked_posts") {
    if (startAuthor == "" && startPermlink == "") {
      body = {
        method: plugin + "." + method,
        id: 2,
        params: {
          sort: filter,
          tag: tag,
          limit: limit
        }
      };
    } else {
      body = {
        method: plugin + "." + method,
        id: 2,
        params: {
          sort: filter,
          tag: tag,
          limit: limit,
          start_author: startAuthor,
          start_permlink: startPermlink
        }
      };
    }
  } else {
    body = {
      method: plugin + "." + method,
      id: 2,
      params: {
        sort: filter,
        account: tag,
        limit: limit,
        start_author: startAuthor,
        start_permlink: startPermlink
      }
    };

  }



  try {
    let response = [];
    if (filter == "top") { // get top posts from db
      response = await db.top_posts_latest(limit);
    } else if (filter == "alltime") {
      response = await db.top_posts_alltime(limit);
    } else {
      response = await apiService.post({
        url: `${getSteemConfig()}`,
        body: body
      });
    }

    if (response && Array.isArray(response)) {
      const processedPosts = processPosts(response);
      let lastPost = null;
      if (processedPosts.length > 0) {
        lastPost = processedPosts[processedPosts.length - 1];
        paginationCache[tag] = {
          lastAuthor: lastPost.author,
          lastPermlink: lastPost.permlink,
        }
      }
      return {
        posts: processedPosts || [],
        lastAuthor: (lastPost) ? lastPost.author : "",
        lastPermlink: (lastPost) ? lastPost.permlink : "",
        hasMore: response.length >= limit
      };
    } else {
      console.log(`No posts found for tag: ${tag}`, response);
      return { posts: [], lastAuthor: "", lastPermlink: "", hasMore: false };
    }
  } catch (error) {
    console.error(`Error fetching posts for tag ${tag}:`, error);
    return { posts: [], lastAuthor: "", lastPermlink: "", hasMore: false };
  }
}
export function processPosts(response) {
  const processedPosts = response.map((post: any) => {
    let metadata = post.json_metadata || {};
    if (typeof metadata === "string") {
      try {
        metadata = (typeof metadata !== "string" || !metadata || metadata.trim() === "undefined") ? {} : JSON.parse(metadata);
      } catch {
        metadata = {};
      }
    }

    let { videoId, idString } = extractYouTubeLink(post.body, metadata);
    const imageMatches = [...post.body.matchAll(/(?:!\[.*?\]\()?(https?:\/\/[^\s)"']+\.(?:png|jpe?g|gif))(?:\))?/gi)];
    let images = [];
    if (imageMatches) {
      images = imageMatches.map(match => match[1])
        .map(url =>
          url
            .replace(/^[('"`]+/, '')
            .replace(/[)'"`]+$/, '')
        );
    }

    let cover = metadata.image ? metadata.image[0] : images[0] || null;

    if (!cover && idString) {
      cover = getYouTubeThumbnailUrl(idString);
    }

    return {
      ...post,
      video: videoId,
      cover: cover,
      images: images,
      id: post.id || `unique-${post.author}-${post.permlink}`,
    };
  });
  return processedPosts;
  //.filter((p: any) => p.video != null);
}
export async function getPosts(
  limit: number,
  tag: string,
  filter: string = 'trending',
  resetPagination: boolean = false,
  lastAuthor: string = '',
  lastPermlink: string = '',
  method: string = 'get_ranked_posts',
  video_only: boolean = true,
  username: string = ''
): Promise<any> {
  let steemVideoPosts;

  if (filter === 'foryou') {
    const start = resetPagination ? 0 : (paginationCache['foryou']?.start || 0);
    const posts = await db.custom_post_queue(username, limit, start);

    paginationCache['foryou'] = {
      lastAuthor: posts.length > 0 ? posts[posts.length - 1].author : '',
      lastPermlink: posts.length > 0 ? posts[posts.length - 1].permlink : '',
      start: start + posts.length
    } as any;

    steemVideoPosts = {
      posts: posts,
      lastAuthor: paginationCache['foryou'].lastAuthor,
      lastPermlink: paginationCache['foryou'].lastPermlink,
      hasMore: posts.length >= limit
    };
  } else {
    steemVideoPosts = await fetchPostsByTag(tag, limit, filter, resetPagination, lastAuthor, lastPermlink, method);
  }

  const filtered = (video_only) ? steemVideoPosts.posts.filter((p: any) => p.video != null) : steemVideoPosts.posts;

  return {
    posts: filtered,
    lastAuthor: steemVideoPosts.lastAuthor,
    lastPermlink: steemVideoPosts.lastPermlink,
    hasMore: steemVideoPosts.hasMore
  };
}
export async function getContent(author: string, permlink: string): Promise<any> {
  let result: any;
  await apiService.post({
    url: `${getSteemConfig()}`,
    body: {
      method: "condenser_api.get_content",
      id: 2,
      params: [author, permlink]
    }
  })
    .then((data: any) => {
      let post = data;

      let metadata = null;
      if (post.json_metadata) {
        try {
          metadata = (typeof post.json_metadata !== "string" || !post.json_metadata || post.json_metadata.trim() === "undefined") ? {} : JSON.parse(post.json_metadata);
        } catch {
          metadata = {};
        }
      }

      let ipfsUrl = extractIPFSLink(post.body, metadata);

      let videoId = ipfsUrl, idString = null;
      if (!ipfsUrl) {
        ({ videoId, idString } = extractYouTubeLink(post.body, metadata));
      }

      let cover = null;
      let images = [];
      if (metadata && metadata.image) {
        cover = metadata.image[0];
      } else {
        const imageMatches = [...post.body.matchAll(/(?:!\[.*?\]\()?(https?:\/\/[^\s)"']+\.(?:png|jpe?g|gif))(?:\))?/gi)];

        images = imageMatches.map(match => match[1])
          .map(url =>
            url
              .replace(/^[('"`]+/, '')
              .replace(/[)'"`]+$/, '')
          );

        if (images && images[0]) {
          cover = images[0];
        }
      }
      if (!cover && idString) {
        cover = getYouTubeThumbnailUrl(idString);
      }
      result = {
        ...data,
        video: videoId ? videoId : null,
        cover: cover,
        images: images
      };
    })
    .catch(() => {
      return [];
    })
  return result;
};
export function getTimeAgo(timestamp: string) {
  const createdAtUTC = new Date(timestamp + 'Z');
  const now = new Date();
  const timeDifferenceMillis = now.getTime() - createdAtUTC.getTime();

  const secondsAgo = Math.floor(timeDifferenceMillis / 1000);
  const minutesAgo = Math.floor(secondsAgo / 60);
  const hoursAgo = Math.floor(minutesAgo / 60);
  const daysAgo = Math.floor(hoursAgo / 24);
  const weeksAgo = Math.floor(daysAgo / 7);
  const monthsAgo = Math.floor(daysAgo / 30);
  const yearsAgo = Math.floor(daysAgo / 365);

  let timeAgoString;
  if (yearsAgo > 0) {
    timeAgoString = `${yearsAgo} year${yearsAgo > 1 ? 's' : ''} ago`;
  } else if (monthsAgo > 0) {
    timeAgoString = `${monthsAgo} month${monthsAgo > 1 ? 's' : ''} ago`;
  } else if (weeksAgo > 0) {
    timeAgoString = `${weeksAgo} week${weeksAgo > 1 ? 's' : ''} ago`;
  } else if (daysAgo > 0) {
    timeAgoString = `${daysAgo} day${daysAgo > 1 ? 's' : ''} ago`;
  } else if (hoursAgo > 0) {
    timeAgoString = `${hoursAgo} hour${hoursAgo > 1 ? 's' : ''} ago`;
  } else if (minutesAgo > 0) {
    timeAgoString = `${minutesAgo} minute${minutesAgo > 1 ? 's' : ''} ago`;
  } else {
    timeAgoString = `${secondsAgo} second${secondsAgo !== 1 ? 's' : ''} ago`;
  }
  return timeAgoString;
}
export function getRemainingTime(timestamp: string) {
  const atUTC = new Date(timestamp + 'Z');
  const now = new Date();
  const timeDifferenceMillis = atUTC.getTime() - now.getTime();

  const secondsTo = Math.floor(timeDifferenceMillis / 1000);
  const minutesTo = Math.floor(secondsTo / 60);
  const hoursTo = Math.floor(minutesTo / 60);
  const daysTo = Math.floor(hoursTo / 24);

  let timeToString;
  if (daysTo > 0) {
    timeToString = `in ${daysTo} day${daysTo > 1 ? 's' : ''}.`;
  } else if (hoursTo > 0) {
    timeToString = `in ${hoursTo} hour${hoursTo > 1 ? 's' : ''}.`;
  } else if (minutesTo > 0) {
    timeToString = `in ${minutesTo} minute${minutesTo > 1 ? 's' : ''}.`;
  } else {
    timeToString = `in ${secondsTo} second${secondsTo !== 1 ? 's' : ''}.`;
  }
  return timeToString;
}
export function getTimeToExpiration(timestamp: string) {
  const createdAtUTC = new Date(timestamp + 'Z');
  const now = new Date();
  const expirationTimeMillis = createdAtUTC.getTime() + 7 * 24 * 60 * 60 * 1000; // Añadir 7 días en milisegundos
  const timeLeftMillis = expirationTimeMillis - now.getTime();

  const secondsLeft = Math.floor(Math.abs(timeLeftMillis) / 1000);
  const minutesLeft = Math.floor(secondsLeft / 60);
  const hoursLeft = Math.floor(minutesLeft / 60);
  const daysLeft = Math.floor(hoursLeft / 24);

  let timeString;

  if (timeLeftMillis > 0) {
    if (daysLeft > 0) {
      timeString = `Payout in ${daysLeft} day${daysLeft > 1 ? 's' : ''}`;
    } else if (hoursLeft > 0) {
      timeString = `Payout in ${hoursLeft} hour${hoursLeft > 1 ? 's' : ''}`;
    } else if (minutesLeft > 0) {
      timeString = `Payout in ${minutesLeft} minute${minutesLeft > 1 ? 's' : ''}`;
    } else {
      timeString = `Payout in ${secondsLeft} second${secondsLeft !== 1 ? 's' : ''}`;
    }
  } else {
    const yearsAgo = Math.floor(Math.abs(timeLeftMillis) / (1000 * 60 * 60 * 24 * 365));
    const daysAgo = Math.floor(Math.abs(timeLeftMillis) / (1000 * 60 * 60 * 24));
    const hoursAgo = Math.floor(Math.abs(timeLeftMillis) / (1000 * 60 * 60)) % 24;
    const minutesAgo = Math.floor(Math.abs(timeLeftMillis) / (1000 * 60)) % 60;

    let expiredString = 'Paid ';
    if (yearsAgo > 0) {
      expiredString += `${yearsAgo} year${yearsAgo > 1 ? 's' : ''} ago`;
    } else if (daysAgo > 0) {
      expiredString += `${daysAgo} day${daysAgo > 1 ? 's' : ''} ago`;
    } else if (hoursAgo > 0) {
      expiredString += `${hoursAgo} hour${hoursAgo > 1 ? 's' : ''} ago`;
    } else if (minutesAgo > 0) {
      expiredString += `${minutesAgo} minute${minutesAgo > 1 ? 's' : ''} ago`;
    } else {
      expiredString += `${secondsLeft} second${secondsLeft !== 1 ? 's' : ''} ago`;
    }
    timeString = expiredString;
  }

  return timeString;
} 